import { useEffect, useState } from "react";
import { viewingCode } from "./viewing.ts";

// A few screens, so no router library: the path is the state.
export type Route =
  | { name: "trips" }
  // `view`: the trip's view other than the map; `item`: what's open.
  // `attach`: a link to attach a file there ("1" for the open item, or a document's id), or to add a
  // photo of the open item ("photo").
  // On a phone, `list`: the trip's list over the map (with `item`, over that place in brief, still
  // selected). `full`: the open item's card in full (on a computer, in the list's place at the map's
  // right; without it, a computer shows it in brief, its row lit in the list).
  // `settings`: Trip settings (the gear beside the trip's name), over whichever view it was opened from.
  // `rate`: the deck, rating what waits on me one at a time (`?view=ideas&rate=1`; with `item` and
  // `full`, that card in full over it).
  // `research`: a finding open to be read in full, over the view (and whatever was open on it: a card it
  // was opened from) as it was (`?view=ideas&research=<id>`); `about`: a card opened from it, over it.
  // `today`: Today, the day from my side, over the view I'm on (`?today=1`; with `item` and `full`, that
  // card in full over it).
  | {
      name: "trip";
      trip: string;
      item: string | null;
      view: string | null;
      attach: string | null;
      list: boolean;
      full: boolean;
      settings: boolean;
      rate: boolean;
      today: boolean;
      research: string | null;
      about: string | null;
    }
  | { name: "join"; code: string }
  // A shared trip's view link (features/sharing.md): the trip's views read-only, by the link's code, with the same
  // steps as a trip's own (`view`, `item`, `list`, `full`, `research`, `about`), for anyone it's shared with.
  | {
      name: "view";
      code: string;
      item: string | null;
      view: string | null;
      list: boolean;
      full: boolean;
      research: string | null;
      about: string | null;
    }
  // The screen after signing up (BeforeWeStart.tsx) under its own address: for an AI's authorization waiting on it
  // (`oauth`: Better Auth's signed request, the address's whole query), for the AIs' "Finish signing up at <link>",
  // and for the server's pages that send people to it first (`next`: where to go back to, a path on this site).
  | { name: "welcome"; oauth: string | null; next: string | null }
  // `how`: How Tripmo works, shown again over the profile; `connect`: Connect your AI, over it, for someone
  // who skipped connecting (Your trips' line on starting a trip of one's own opens it too).
  | { name: "profile"; how: boolean; connect: boolean }
  // A link shared to Tripmo from a phone's share sheet (the manifest's share_target): what was shared, to save to a
  // trip's links inbox (ShareLink.tsx).
  | { name: "share"; url: string | null; text: string | null; title: string | null };

// The route the address names now.
export function parse(): Route {
  if (/^\/welcome\/?$/.test(location.pathname)) {
    const params = new URLSearchParams(location.search);
    const next = params.get("next");
    return {
      name: "welcome",
      oauth: params.has("client_id") && params.has("sig") ? location.search.slice(1) : null,
      // Only a path on this site, never somewhere else (the sign-in page's rule).
      next: next?.startsWith("/") && !next.startsWith("//") && !next.startsWith("/\\") ? next : null,
    };
  }
  if (/^\/profile\/?$/.test(location.pathname)) {
    const params = new URLSearchParams(location.search);
    return { name: "profile", how: params.get("how") === "1", connect: params.get("connect") === "1" };
  }
  if (/^\/share\/?$/.test(location.pathname)) {
    const params = new URLSearchParams(location.search);
    return { name: "share", url: params.get("url"), text: params.get("text"), title: params.get("title") };
  }
  const shared = location.pathname.match(/^\/v\/([^/]+)\/?$/);
  if (shared?.[1]) {
    const params = new URLSearchParams(location.search);
    return {
      name: "view",
      code: decodeURIComponent(shared[1]),
      item: params.get("item"),
      view: params.get("view"),
      list: params.get("list") === "1",
      full: params.get("card") === "full",
      research: params.get("research"),
      about: params.get("about"),
    };
  }
  const join = location.pathname.match(/^\/j\/([^/]+)\/?$/);
  if (join?.[1]) return { name: "join", code: decodeURIComponent(join[1]) };
  const match = location.pathname.match(/^\/t\/([^/]+)\/?$/);
  if (match?.[1]) {
    const params = new URLSearchParams(location.search);
    return {
      name: "trip",
      trip: decodeURIComponent(match[1]),
      item: params.get("item"),
      view: params.get("view"),
      attach: params.get("attach"),
      list: params.get("list") === "1",
      full: params.get("card") === "full",
      settings: params.get("settings") === "1",
      rate: params.get("rate") === "1",
      today: params.get("today") === "1",
      research: params.get("research"),
      about: params.get("about"),
    };
  }
  return { name: "trips" };
}

export function useRoute(): Route {
  const [route, setRoute] = useState(parse);
  useEffect(() => {
    const update = () => setRoute(parse());
    addEventListener("popstate", update);
    addEventListener("tripmo:navigate", update);
    return () => {
      removeEventListener("popstate", update);
      removeEventListener("tripmo:navigate", update);
    };
  }, []);
  return route;
}

// Changes the address without a reload: a new step in the history, so Back returns here, or with
// `replace` the same step. `state` rides along with the step (see `stepState`).
export function navigate(to: string, options: { replace?: boolean; state?: StepState } = {}) {
  if (to === location.pathname + location.search) return;
  // What the lists on the step being left look like, written down before it's left.
  for (const keep of keepers) keep();
  // And what the new step starts with (kept.ts: each view where it was last left), under what it's given.
  const carried = Object.assign({}, ...[...carriers].map((carry) => carry(to))) as Record<string, unknown>;
  const lists = { ...carried, ...options.state?.lists };
  const state = { ...options.state, ...(Object.keys(lists).length > 0 && { lists }), key: newKey() };
  if (options.replace) history.replaceState(state, "", to);
  else history.pushState(state, "", to);
  dispatchEvent(new Event("tripmo:navigate"));
  for (const changed of changes) changed();
}

// Called before every step: lists write down where they were (kept.ts).
const keepers = new Set<() => void>();
export function beforeEachStep(keep: () => void): () => void {
  keepers.add(keep);
  return () => keepers.delete(keep);
}

// Asked for what each new step's lists start as, given where it goes (kept.ts).
const carriers = new Set<(to: string) => Record<string, unknown>>();
export function carryEachStep(carry: (to: string) => Record<string, unknown>) {
  carriers.add(carry);
}

// Called after the address or what the current step remembers changes (reopen.ts keeps it).
const changes = new Set<() => void>();
export function whenStepChanges(changed: () => void) {
  changes.add(changed);
}

// What a step remembers beyond its address: where the map was when it was last on it (so a reload finds
// it there; Back never moves the map, reopen.ts keeps where it was last left), how many items
// were stepped through one after another to get here (items, and a card opened in full), whether the
// first was opened from the map's list (so closing can go back past all of them to it), whether this
// step opened something over the map on a phone (so closing it is going Back), whether it opened in full
// on a computer the item the step before showed in brief (so closing it is going Back too), whether a
// card in full was opened from a row of a phone's list over the map (so its X goes back to that list)
// and how far the map's list was scrolled then (so it comes back as it was), the choice whose
// options are drawn on the map (shown from Ideas), and how each list was (kept.ts): its scroll and
// what was open; on Your trips, the trip just deleted, for its Undo; on the deck, the run through it so
// far (deck.ts), so Back, Forward and a reload find it as it was; what Today's "All on the map" framed.
// `key` names the step; `shown`, that its trip has shown it (reopen.ts).
export interface StepState {
  key?: string;
  // `framed`: where the map's own frame of the whole trip put it, never moved since (TripMap).
  camera?: { lng: number; lat: number; zoom: number; framed?: boolean };
  opened?: number;
  fromList?: boolean;
  layer?: boolean;
  fromBrief?: boolean;
  fromRow?: boolean;
  listTop?: number;
  compare?: string;
  lists?: Record<string, unknown>;
  deleted?: { id: string; title: string } | undefined;
  shown?: boolean;
  deck?: unknown;
  // Over the deck, how many cards in full were opened one after another over it, so a rating that answers
  // the deck closes them all back to it.
  overDeck?: number;
  // On the map, what Today's "All on the map" asked to see together, framed (Today.tsx).
  around?: string[];
}

export function stepState(): StepState {
  return (history.state as StepState | null) ?? {};
}

const newKey = () => Math.random().toString(36).slice(2, 10);

// The current step's name; a page just opened gets one.
export function stepKey(): string {
  const key = stepState().key;
  if (key) return key;
  const fresh = newKey();
  history.replaceState({ ...stepState(), key: fresh }, "");
  return fresh;
}

export function rememberInStep(patch: StepState) {
  history.replaceState({ ...stepState(), ...patch }, "");
  for (const changed of changes) changed();
}

export const tripPath = (
  trip: string,
  item?: string | null,
  view?: string | null,
  over: {
    list?: boolean;
    full?: boolean;
    settings?: boolean;
    rate?: boolean;
    today?: boolean;
    research?: string | null;
    about?: string | null;
  } = {},
) => {
  const params = new URLSearchParams();
  if (view && view !== "map") params.set("view", view);
  if (over.rate) params.set("rate", "1");
  if (over.today) params.set("today", "1");
  if (item) params.set("item", item);
  if (over.list) params.set("list", "1");
  if (item && over.full) params.set("card", "full");
  if (over.settings) params.set("settings", "1");
  if (over.research) params.set("research", over.research);
  if (over.research && over.about) params.set("about", over.about);
  const query = params.toString();
  // On a view link, the trip is its code, and its steps are the link's (viewing.ts).
  const at = viewingCode() === trip ? "v" : "t";
  return `/${at}/${encodeURIComponent(trip)}${query ? `?${query}` : ""}`;
};
